import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardJump from '@swf/core/hooks/useWizardJump';
import { select } from '@swf/core/store';

import type {
  DepositResult,
  SensitiveDepositPayload,
  WorldPay3DS2Info
} from '../../../../../../../../types/Cashier';
import { ChallengeWindowSize } from '../../../../../../../../utils/browserExtraInfo';
import { DDCStatus } from '../../RejectedX3dSecure2RequiredError';
import DDCStatusMessages from '../DDCStatusMessages/DDCStatusMessages';

interface DDCOutcome {
  MessageType: string;
  SessionId: string;
  Status: boolean;
}

const timeout = 10000;

const WorldPay: React.FC<DepositResult> = ({ resultInfo }) => {
  const {
    data: { deposit },
    values
  } = useWizardContext();
  const { url: ddcUrl, jwt } = (resultInfo as WorldPay3DS2Info) || {};
  const pan = (deposit as SensitiveDepositPayload).accountNumber || deposit.savedCardInfoId;
  const [resultDDCStatus, setResultDDCStatus] = useState<DDCStatus>();
  const isMobile = useSelector(select.device.isMobile);
  const [contentRef, setContentRef] = useState<HTMLIFrameElement | null>(null);
  const mountNode = contentRef?.contentWindow?.document?.body;

  const { jumpForward } = useWizardJump();

  const worldPayProcess = (sessionId: string) => {
    values.deposit = {
      ...deposit,
      worldpay3DS2ReferenceId: sessionId,
      worldpay3DS2WindowSize: isMobile
        ? ChallengeWindowSize.PSP3DS2FullPage
        : ChallengeWindowSize.PSP3DS2
    };

    jumpForward('#deposit.deposit-process');
  };

  const getDDCForm = () => (
    <form id="collectionForm" method="POST" action={ddcUrl}>
      <input type="hidden" name="Bin" value={pan} />
      <input type="hidden" name="JWT" value={jwt} />
    </form>
  );

  const appendScript = () => {
    const script: HTMLScriptElement | undefined =
      contentRef?.contentWindow?.document.createElement('script');
    if (script && mountNode) {
      script.innerHTML = "document.getElementById('collectionForm').submit();";
      mountNode.appendChild(script);

      return () => {
        mountNode.removeChild(script);
      };
    }
  };

  useEffect(() => {
    const timer = setTimeout(
      () => !resultDDCStatus && setResultDDCStatus(DDCStatus.ddcTimeout),
      timeout
    );

    const clear = () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timer);
    };

    const handleMessage = async (event: MessageEvent) => {
      const { origin } = new URL(ddcUrl);
      if (origin === event.origin) {
        const { Status, SessionId } = JSON.parse(event.data) as DDCOutcome;

        if (Status) {
          setResultDDCStatus(DDCStatus.ddcOk);
          worldPayProcess(SessionId);
          return;
        }

        setResultDDCStatus(DDCStatus.ddcError);
        clear();
      }
    };

    if (!resultInfo || !jwt || !pan || !ddcUrl) {
      setResultDDCStatus(DDCStatus.ddcError);
      clear();
    } else if (!values.deposit.worldpay3DS2ReferenceId) {
      window.addEventListener('message', handleMessage);
    }

    return clear;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (contentRef) {
      appendScript();
      setContentRef(null);
    }
  }, [contentRef]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <DDCStatusMessages ddcStatus={resultDDCStatus} />
      <iframe
        title="ddcChallenge"
        height="1"
        width="1"
        style={{ display: 'none' }}
        ref={setContentRef}
      >
        {mountNode && createPortal(getDDCForm(), mountNode)}
      </iframe>
    </>
  );
};

export default WorldPay;
